// #485 — one-shot guard against the `/c/:code/resume ↔ /signin` ping-pong.
//
// The funnel auto-proceeds on the resume route (and in AutoForward mode). When `proceed()` finds no
// session it redirects to accounts-web `/signin?redirect_uri=…/resume`; the resume route re-mounts and
// auto-proceeds again. If the end-user session cookie never becomes readable in this jar (the failure
// mode ADR 0136 describes) that repeats forever with no user interaction.
//
// So we record that we already spent our one automatic sign-in round-trip for this campaign code. A
// second unauthenticated arrival stops and asks the person to sign in explicitly instead of redirecting
// again. Kept in sessionStorage (per-tab, dies with the tab) and keyed by code so two different campaigns
// don't starve each other. Every access is try/caught — a blocked storage jar must not break the funnel,
// and degrading to "no guard" is the pre-existing behaviour, never worse.

const key = (code: string) => `wa-campaign-signin-attempt:${code}`

export function hasAttemptedSignIn(code: string): boolean {
  try {
    return sessionStorage.getItem(key(code)) === '1'
  } catch {
    return false
  }
}

export function markSignInAttempted(code: string): void {
  try {
    sessionStorage.setItem(key(code), '1')
  } catch {
    // ignore — a blocked jar just means no guard, i.e. today's behaviour
  }
}

export function clearSignInAttempt(code: string): void {
  try {
    sessionStorage.removeItem(key(code))
  } catch {
    // ignore
  }
}

/**
 * What an unauthenticated `proceed()` should do for this code: take the one automatic sign-in
 * round-trip, or stop and hand the person an explicit "Entrar" button.
 */
export function unauthenticatedAction(code: string, attempted: boolean = hasAttemptedSignIn(code)): 'redirect' | 'manual' {
  return attempted ? 'manual' : 'redirect'
}
